/**
 * Championship match-day line-up: ranking order, minus season absences,
 * minus players marked out for this fixture. Empty seats fill from the next
 * available player (lower team / reserve).
 */

import { sortPlayersByRankingPoints } from "./championshipMatchSheet";

export function rankedPool(view) {
  const groups = view?.teams ?? view?.roster_by_team ?? [];
  const list = Array.isArray(groups) ? groups : Object.values(groups || {});
  const teams = [...list].sort(
    (a, b) => Number(a.team_number) - Number(b.team_number)
  );
  const phaseOut = new Set(
    [
      ...(view?.absent || []),
      ...(view?.benched || []),
      ...(view?.unknown || []),
      ...(view?.roster_absent || []),
      ...(view?.roster_benched || []),
      ...(view?.roster_unknown || []),
    ]
      .map((p) => String(p?.user_id || ""))
      .filter(Boolean)
  );
  const pool = [
    ...teams.flatMap((t) => t.players || []),
    ...(view?.reserve || []),
    ...(view?.roster_reserve || []),
  ].filter((p) => p?.user_id && !p?.is_guest && !phaseOut.has(String(p.user_id)));
  return sortPlayersByRankingPoints(pool);
}

/**
 * Cascade next-match selections from team 1 → N.
 * Players selected for a superior team are locked for lower divisions.
 * Players marked out on a superior team stay available below.
 */
export function cascadeMatchSquad(rankingPool, teamNumber, outsByTeam = {}, teamSize = 4) {
  let remaining = [...(rankingPool || [])].filter((p) => p?.user_id);
  const lockedFromSuperior = new Set();
  const n = Math.max(1, Number(teamNumber) || 1);
  const size = Math.max(1, Number(teamSize) || 4);
  let squad = [];

  for (let t = 1; t <= n; t += 1) {
    const raw = outsByTeam[t] ?? outsByTeam[String(t)] ?? [];
    const outs = new Set([...raw].map(String).filter(Boolean));
    const available = remaining.filter((p) => !outs.has(String(p.user_id)));
    const four = available.slice(0, size);
    if (t === n) {
      squad = four;
      break;
    }
    const selected = new Set(four.map((p) => String(p.user_id)));
    for (const id of selected) lockedFromSuperior.add(id);
    remaining = remaining.filter((p) => !selected.has(String(p.user_id)));
  }

  return { squad, lockedFromSuperior, remainingAfter: remaining };
}

export function presenceOutsFromRsvps(rsvpRows) {
  return (Array.isArray(rsvpRows) ? rsvpRows : [])
    .filter((r) => r.status === "out")
    .map((r) => String(r.user_id))
    .filter(Boolean);
}

export function matchOutIds(rsvps) {
  return new Set(
    (Array.isArray(rsvps) ? rsvps : [])
      .filter((r) => r?.status === "out")
      .map((r) => String(r.user_id))
  );
}

export function availableForMatch(view, rsvps) {
  const out = matchOutIds(rsvps);
  return rankedPool(view).filter((p) => !out.has(String(p.user_id)));
}

export function lineupForTeam(available, teamNumber, teamSize = 4) {
  const n = Math.max(1, Number(teamNumber) || 1);
  const size = Math.max(1, Number(teamSize) || 4);
  const start = (n - 1) * size;
  return available.slice(start, start + size);
}

export function isCalledUp(player, teamNumber) {
  if (!player) return false;
  const seasonTeam = player.team_number;
  if (seasonTeam == null || seasonTeam === "") return true;
  return Number(seasonTeam) !== Number(teamNumber);
}

export function playersOutThisMatch(view, rsvps, teamNumber) {
  const out = matchOutIds(rsvps);
  const n = Number(teamNumber);
  return rankedPool(view).filter((p) => {
    if (!out.has(String(p.user_id))) return false;
    if (n && Number(p.team_number) !== n) return false;
    return true;
  });
}

export function nextAvailableAfterLineup(available, teamNumber, teamSize = 4) {
  const size = Math.max(1, Number(teamSize) || 4);
  const end = Number(teamNumber) * size;
  return available[end] || null;
}

export function emptySeats(lineup, teamSize = 4) {
  const size = Math.max(1, Number(teamSize) || 4);
  return Math.max(0, size - (lineup?.length || 0));
}
